import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { vorgangService, type TodoDto } from "../../../services/vorgangService";
import { labelForUserKey, type AppUser } from "../../../services/userService";
import {
    zusammenfuehrenAuswahl,
    zusammenfuehrenEntwurf,
    zusammenfuehrenFehler,
    zusammenfuehrenFolgeText,
    type ZusammenfuehrenEntwurf,
} from "./todoZusammenfuehren";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";
import { twvPatch } from "../shared/fristZurTwv";
import { alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * „To-do zusammenführen…“ (PO 16.09.2026).
 *
 * > „das man todos im vorgang zusammenführen kann mit ⋯, dann neben der Option
 * > entfernen: To-do zusammenführen, und dann Popup mit welcher Betreff (kann
 * > man auch ändern), Hauptverantwortlicher, Bearbeiter, … Zuweisung beides
 * > das bitte auch.“
 *
 * ══ WARUM EIN DIALOG UND KEIN STILLER BEFEHL ═══════════════════════════════
 * Zusammenführen ist die einzige Handlung in dieser Ansicht, die sich nicht
 * mit einem Klick zurückholen lässt: die Quelle ist danach zwar nur weich
 * gelöscht und wiederherstellbar, die umgehängten Mails, Dokumente und Notizen
 * kommen mit ihr aber NICHT zurück. Wer zwei To-dos verschmilzt, soll vorher
 * sehen, was danach dasteht — und die Fußzeile sagt die Folge im Klartext.
 *
 * ══ DIE OPTIK ═════════════════════════════════════════════════════════════
 * Dieselbe Hülle wie `DokumentUmbenennenDialog` und `AlleListeDialog`:
 * Portal an `document.body`, eigene `.kvp-rd`-Schicht, `vg4-schleier`, ESC und
 * Klick daneben schließen. Bewusst KEIN Fluent-Dialog (PO 09.09.2026).
 *
 * Die Ableitung — Vorbelegung, Vereinigung, der Warnfall „zwei Kunden“ —
 * steht in `todoZusammenfuehren.ts` und wird dort geprüft. Hier steht nur,
 * wie sie aussieht.
 *
 * ══ WER IST ZIEL? (PO 28.09.2026) ═════════════════════════════════════════
 * „Bei To-do zusammenführen ist der Betreff der des ursprünglichen To-dos als
 * Vorschlag, sollte aber Ziel-To-do vorschlagen." Bis dahin blieb das
 * ANGEKLICKTE To-do bestehen, und sein Betreff stand im Feld — der Anwender
 * denkt aber „dieses hier IN jenes": Das angeklickte ist die Quelle, das in
 * der Liste gewählte das Ziel. Seitdem bleibt das gewählte bestehen und
 * bringt seinen Betreff mit. Dieselbe Richtung wie beim Ziehen: Das
 * gezogene To-do geht in dem auf, auf das es fällt (`vorgewaehltesZielId`).
 *
 * ══ BETREFF BEIM ZIEHEN (T-15, 28.09.2026) ════════════════════════════════
 * Beim Ziehen gilt der Betreff der QUELLE, also des gezogenen To-dos
 * (`betreffVon="quelle"`) — auch nach einem Zielwechsel in der Liste. Das
 * getroffene To-do bleibt technisch bestehen und bekommt nur den Betreff.
 * Der ⋯-Weg bleibt beim Ziel-Betreff. Unterscheiden sich die beiden, lässt
 * sich der andere über „Betreff übernehmen von" mit einem Klick holen.
 */
type Props = {
    /** Das ANGEKLICKTE (oder gezogene) To-do — es geht im Ziel auf. */
    quelle: TodoDto;
    /** Die übrigen To-dos des Vorgangs, je mit „2 Notizen · 5 im Verlauf“ — die möglichen Ziele. */
    kandidaten: Array<{ todo: TodoDto; zaehlung: string }>;
    /** Beim Ziehen: das To-do, auf das losgelassen wurde. Sonst das erste der Liste. */
    vorgewaehltesZielId?: number | null;
    /** Wessen Betreff vorbelegt wird: `"quelle"` beim Ziehen (T-15), sonst das Ziel. */
    betreffVon?: "ziel" | "quelle";
    users: AppUser[];
    /** Nach erfolgreichem Zusammenführen — die Seite lädt dann neu. */
    onZusammengefuehrt: () => void | Promise<void>;
    onSchliessen: () => void;
};

/** Ein an-/abwählbarer Personeneintrag. */
const PersonChip: React.FC<{
    id: string;
    users: AppUser[];
    gewaehlt: boolean;
    disabled?: boolean;
    onToggle: () => void;
}> = ({ id, users, gewaehlt, disabled, onToggle }) => (
    <label className={`vg4-zf-chip${gewaehlt ? " an" : ""}`}>
        <input type="checkbox" checked={gewaehlt} disabled={disabled} onChange={onToggle} />
        <span>{labelForUserKey(id, users) || id}</span>
    </label>
);

export const TodoZusammenfuehrenDialog: React.FC<Props> = ({
    quelle,
    kandidaten,
    vorgewaehltesZielId,
    betreffVon = "ziel",
    users,
    onZusammengefuehrt,
    onSchliessen,
}) => {
    const [zielId, setZielId] = useState<number | null>(
        (vorgewaehltesZielId != null && kandidaten.some((k) => k.todo.id === vorgewaehltesZielId)
            ? vorgewaehltesZielId
            : null) ??
            kandidaten[0]?.todo.id ??
            null,
    );
    const ziel = useMemo(
        () => kandidaten.find((k) => k.todo.id === zielId)?.todo ?? null,
        [kandidaten, zielId],
    );
    const auswahl = useMemo(
        () => (ziel ? zusammenfuehrenAuswahl(ziel, quelle) : null),
        [ziel, quelle],
    );
    const [entwurf, setEntwurf] = useState<ZusammenfuehrenEntwurf | null>(
        ziel ? zusammenfuehrenEntwurf(ziel, quelle, { betreffVon }) : null,
    );
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    // Ein Wechsel des Ziels baut die Vorbelegung neu auf: Vereinigung und
    // Kundenfrage hängen an BEIDEN Seiten. Der Betreff folgt dem Ziel — außer
    // beim Ziehen, da bleibt er der des gezogenen To-dos (T-15).
    useEffect(() => {
        setEntwurf(ziel ? zusammenfuehrenEntwurf(ziel, quelle, { betreffVon }) : null);
        setFehler(null);
    }, [ziel, quelle, betreffVon]);

    useEffect(() => {
        const auf = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !busy) onSchliessen();
        };
        alleFenster.addEventListener("keydown", auf);
        return () => alleFenster.removeEventListener("keydown", auf);
    }, [busy, onSchliessen]);

    const setze = (teil: Partial<ZusammenfuehrenEntwurf>) =>
        setEntwurf((e) => (e ? { ...e, ...teil } : e));

    const toggleId = (feld: "hauptverantwortlich" | "bearbeiter" | "zuInformierende", id: string) =>
        setEntwurf((e) =>
            e
                ? {
                      ...e,
                      [feld]: e[feld].includes(id) ? e[feld].filter((x) => x !== id) : [...e[feld], id],
                  }
                : e,
        );

    const toggleBezug = (feld: "risikoIds" | "vertragIds", id: number) =>
        setEntwurf((e) =>
            e
                ? {
                      ...e,
                      [feld]: e[feld].includes(id) ? e[feld].filter((x) => x !== id) : [...e[feld], id],
                  }
                : e,
        );

    // Die beiden Betreffs zur Wahl — nur, wenn sie sich unterscheiden.
    const betreffWahl = useMemo(() => {
        const q = quelle.titel?.trim() ?? "";
        const z = ziel?.titel?.trim() ?? "";
        if (!q || !z || q === z) return [];
        return [
            { titel: q, rolle: betreffVon === "quelle" ? "gezogenes To-do" : "dieses To-do" },
            { titel: z, rolle: betreffVon === "quelle" ? "Ziel-To-do" : "gewähltes To-do" },
        ];
    }, [quelle.titel, ziel?.titel, betreffVon]);

    const sperre = entwurf && auswahl ? zusammenfuehrenFehler(entwurf, auswahl) : "Kein zweites To-do vorhanden.";

    const speichern = async () => {
        if (busy || !entwurf || !ziel || sperre) return;
        setBusy(true);
        setFehler(null);
        try {
            await vorgangService.mergeTodo(ziel.id, {
                quelleTodoId: quelle.id,
                titel: entwurf.titel.trim(),
                hauptverantwortlich: entwurf.hauptverantwortlich,
                bearbeiter: entwurf.bearbeiter,
                zuInformierende: entwurf.zuInformierende,
                kontaktId: entwurf.kontaktId,
                risikoIds: entwurf.risikoIds,
                vertragIds: entwurf.vertragIds,
                twv: entwurf.twv,
                frist: entwurf.frist,
            });
        } catch (err: any) {
            // Offen bleiben: die getroffene Auswahl ist sonst weg, und der
            // Nutzer wüsste nicht einmal, dass nichts ankam.
            console.error("[TodoZusammenfuehren] Fehlgeschlagen", err);
            setFehler(err?.message ?? "Zusammenführen fehlgeschlagen.");
            setBusy(false);
            return;
        }
        await onZusammengefuehrt();
        onSchliessen();
    };

    const personenZeile = (
        label: string,
        feld: "hauptverantwortlich" | "bearbeiter" | "zuInformierende",
        ids: string[],
    ) =>
        ids.length > 0 && (
            <div className="vg4-zf-zeile">
                <span className="vg4-zf-label">{label}</span>
                <div className="vg4-zf-chips" role="group" aria-label={label}>
                    {ids.map((id) => (
                        <PersonChip
                            key={id}
                            id={id}
                            users={users}
                            gewaehlt={!!entwurf?.[feld].includes(id)}
                            disabled={busy}
                            onToggle={() => toggleId(feld, id)}
                        />
                    ))}
                </div>
            </div>
        );

    return createPortal(
        <div
            className="vg4-schleier"
            role="presentation"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !busy) onSchliessen();
            }}
        >
            <div
                className="kvp-rd vg4-dialog vg4-zf"
                role="dialog"
                aria-modal="true"
                aria-label="To-do zusammenführen"
            >
                <div className="vg4-umb-kopf">
                    <span className="vg4-dialog-t">To-do zusammenführen</span>
                    <span className="vg4-umb-alt" title={quelle.titel}>
                        geht auf: „{quelle.titel || `To-do ${quelle.id}`}“
                    </span>
                </div>

                <div className="vg4-zf-b">
                    {/* ── Womit? ───────────────────────────────────────────── */}
                    <div className="vg4-zf-zeile spalte">
                        <span className="vg4-zf-label">In welches To-do?</span>
                        <div className="vg4-zf-wahl" role="radiogroup" aria-label="In welches To-do?">
                            {kandidaten.map(({ todo, zaehlung }) => (
                                <label
                                    key={todo.id}
                                    className={`vg4-zf-kandidat${zielId === todo.id ? " an" : ""}`}
                                >
                                    <input
                                        type="radio"
                                        name="vg4-zf-ziel"
                                        checked={zielId === todo.id}
                                        disabled={busy}
                                        onChange={() => setZielId(todo.id)}
                                    />
                                    <span className="vg4-zf-kandidat-t">
                                        {todo.titel || `To-do ${todo.id}`}
                                    </span>
                                    {zaehlung && <span className="vg4-zf-kandidat-z">{zaehlung}</span>}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* ── Betreff ──────────────────────────────────────────── */}
                    {ziel && betreffWahl.length === 2 && (
                        <div className="vg4-zf-zeile">
                            <span className="vg4-zf-label">Betreff von</span>
                            <div className="vg4-zf-chips" role="radiogroup" aria-label="Betreff übernehmen von">
                                {betreffWahl.map(({ titel, rolle }) => (
                                    <label
                                        key={rolle}
                                        className={`vg4-zf-chip${entwurf?.titel === titel ? " an" : ""}`}
                                    >
                                        <input
                                            type="radio"
                                            name="vg4-zf-betreff-von"
                                            checked={entwurf?.titel === titel}
                                            disabled={busy || !entwurf}
                                            onChange={() => setze({ titel })}
                                        />
                                        <span>
                                            „{titel}“ ({rolle})
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="vg4-zf-zeile">
                        <label className="vg4-zf-label" htmlFor="vg4-zf-titel">
                            Betreff
                        </label>
                        <input
                            id="vg4-zf-titel"
                            className="vg4-umb-feld"
                            value={entwurf?.titel ?? ""}
                            disabled={busy || !entwurf}
                            onChange={(e) => setze({ titel: e.target.value })}
                        />
                    </div>

                    {/* ── Rollen ───────────────────────────────────────────── */}
                    {auswahl && personenZeile("Hauptverantwortlich", "hauptverantwortlich", auswahl.hauptverantwortlich)}
                    {auswahl && personenZeile("Bearbeiter", "bearbeiter", auswahl.bearbeiter)}
                    {auswahl && personenZeile("zu informieren", "zuInformierende", auswahl.zuInformierende)}

                    {/* ── Kunde ────────────────────────────────────────────── */}
                    {auswahl && auswahl.kontakte.length > 0 && (
                        <div className="vg4-zf-zeile spalte">
                            <span className="vg4-zf-label">Kunde</span>
                            {auswahl.kundenKonflikt && (
                                <div className="vg4-zf-warnung" role="alert">
                                    Die beiden To-dos hängen an VERSCHIEDENEN Kunden. Ein To-do hat
                                    genau einen — bitte entscheiden, welcher bleibt.
                                </div>
                            )}
                            <div className="vg4-zf-wahl" role="radiogroup" aria-label="Kunde">
                                {auswahl.kontakte.map((k) => (
                                    <label
                                        key={k.id}
                                        className={`vg4-zf-kandidat${entwurf?.kontaktId === k.id ? " an" : ""}`}
                                    >
                                        <input
                                            type="radio"
                                            name="vg4-zf-kunde"
                                            checked={entwurf?.kontaktId === k.id}
                                            disabled={busy}
                                            onChange={() => setze({ kontaktId: k.id })}
                                        />
                                        <span className="vg4-zf-kandidat-t">
                                            {[k.firstName, k.lastname].filter(Boolean).join(" ") ||
                                                `Kontakt ${k.id}`}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Risiken und Verträge ─────────────────────────────── */}
                    {auswahl && auswahl.risiken.length > 0 && (
                        <div className="vg4-zf-zeile">
                            <span className="vg4-zf-label">Risiken</span>
                            <div className="vg4-zf-chips" role="group" aria-label="Risiken">
                                {auswahl.risiken.map((r) => (
                                    <label
                                        key={r.id}
                                        className={`vg4-zf-chip${entwurf?.risikoIds.includes(r.id) ? " an" : ""}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!entwurf?.risikoIds.includes(r.id)}
                                            disabled={busy}
                                            onChange={() => toggleBezug("risikoIds", r.id)}
                                        />
                                        <span>{r.name || r.riskObjectIdentifier || `Risiko ${r.id}`}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    {auswahl && auswahl.vertraege.length > 0 && (
                        <div className="vg4-zf-zeile">
                            <span className="vg4-zf-label">Verträge</span>
                            <div className="vg4-zf-chips" role="group" aria-label="Verträge">
                                {auswahl.vertraege.map((v) => (
                                    <label
                                        key={v.id}
                                        className={`vg4-zf-chip${entwurf?.vertragIds.includes(v.id) ? " an" : ""}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!entwurf?.vertragIds.includes(v.id)}
                                            disabled={busy}
                                            onChange={() => toggleBezug("vertragIds", v.id)}
                                        />
                                        <span>{v.contractNbr || v.branch || `Vertrag ${v.id}`}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── TWV und Frist ────────────────────────────────────── */}
                    <div className="vg4-zf-zeile">
                        <label className="vg4-zf-label" htmlFor="vg4-zf-twv">
                            TWV
                        </label>
                        <input
                            id="vg4-zf-twv"
                            type="date"
                            className="vg4-zf-datum"
                            value={entwurf?.twv ?? ""}
                            disabled={busy}
                            onChange={(e) => setze(twvPatch(e.target.value || null, entwurf?.frist))}
                        />
                        <label className="vg4-zf-label" htmlFor="vg4-zf-frist">
                            Frist
                        </label>
                        <input
                            id="vg4-zf-frist"
                            type="date"
                            className="vg4-zf-datum"
                            value={entwurf?.frist ?? ""}
                            disabled={busy}
                            onChange={(e) => setze({ frist: e.target.value || null })}
                        />
                    </div>

                    {fehler && (
                        <div className="vg4-umb-fehler" role="alert">
                            {fehler}
                        </div>
                    )}
                </div>

                {/* ── Die Folge im Klartext ────────────────────────────────── */}
                <div className="vg4-zf-fuss">
                    <div className="vg4-zf-folge">
                        <strong>
                            {zusammenfuehrenFolgeText(
                                ziel?.titel ?? "",
                                quelle.titel ?? "",
                                entwurf?.titel,
                            )}
                        </strong>
                        <span>
                            Mails, Dokumente, Anrufe, Termine und Notizen wandern mit. Das lässt sich
                            nicht mit einem Klick zurückholen.
                        </span>
                        {sperre && <span className="vg4-zf-sperre">{sperre}</span>}
                    </div>
                    <div className="vg4-zf-knoepfe">
                        <button type="button" className="vg3-btn" disabled={busy} onClick={onSchliessen}>
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            className="vg3-btn primary"
                            disabled={busy || !!sperre}
                            title={sperre ?? undefined}
                            onClick={() => void speichern()}
                        >
                            {busy ? "…" : "Zusammenführen"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};
